/* eslint-disable camelcase */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { Suspense, useEffect, useMemo, useState, useLayoutEffect } from 'react';
import { Switch, Route, useHistory, Redirect, useLocation } from 'react-router-dom';
import ReactNotification from 'react-notifications-component';
import { useQuery, useQueryClient } from 'react-query';
import { isBrowser } from 'react-device-detect';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

// import routers
import LoginRouter from './routers/LoginRouter';
import DashboardRouter from './routers/DashboardRouter';
import ShowCaseRouter from './routers/ShowCaseRouter';
import KYCRoutes from './routers/KycRouter';
import { GenericLoading } from './components/Placeholders';
// import initial screen
import Promo from './screens/Promo';
import SessionTimeout from './screens/SessionTimeout';
import HintRouter from './routers/HintRouter';

// localization config file
import { getCurrentLanguage } from './i18n';
import {
  cacheUserToken,
  getCachedUserToken,
  resetUserSession,
  getCacheUserFirstUsage,
  cacheUserFirstUsage,
  getCacheUserFirstAccess,
  getCacheUserWantsToStayInUSApp,
  getCacheUserWantsToStayInInternationalApp,
} from './utilities/caching';
import {
  USER_CARD_INFO,
  USER_DETAILS_QUERY_KEY,
  USER_PREFERENCES,
} from './utilities/sharedQueries';
import Icons from './screens/Icons';

import {
  getUrlEnvironment,
  handleUserEnvironmentUrl,
  handleWindowViewPort,
} from './utilities/helpers';
import { useSetting } from './utilities/sharedHooks/settings';
import { ACCOUNT_REGIONS, ALLOWED_ROUTES_WITHOUT_TOTP } from './utilities/constants';
import Maintenance from './screens/Maintenance';
import { InitializeGoogleAnalytics } from './utilities/google-analytics';
import UsAppWarningModal from './screens/shared/UsAppWarningModal';
import useUserIPLocation from './utilities/sharedHooks/useUserIPLocation';
import OauthLoginRedirectionComponent from './screens/Oauth/OauthLoginRedirectionComponent';
import OauthRedirectionHandlerComponent from './screens/Oauth/OauthRedirectionHandlerComponent';
import OauthAuthorizeComponent from './screens/Oauth/OauthAuthorizeComponent';
import WithdrawalDeeplink from './screens/Accounts/deeplink';
import TopupDeeplink from './screens/Accounts/topupDeeplink';
import ManageAssetsDeeplink from './screens/Accounts/manageAssetsDeeplink';
import CardOrderDeeplink from './screens/Card/deeplink';
import CashbackDeeplink from './screens/Card/Loyalty/deeplink';
import SetPinDeeplink from './screens/Card/Settings/deeplink';

import { getEnvByAppName } from './utilities/oauthEnvSwitch';
import { OAUTH_SESSION_KEYS } from './screens/Oauth/constants';

const MAIN_DEFAULT_ROUTE = '/promo';
const DEFAULT_ROUTES = [
  '/promo',
  '/login',
  '/onboarding/login',
  '/dashboard/card',
  '/oauth2/interaction',
];
const DEFAULT_LOCALE = 'en';

const IS_DEV =
  !process.env.NODE_ENV ||
  process.env.NODE_ENV === 'development' ||
  (process.env.REACT_APP_ENVIRONMENT && process.env.REACT_APP_ENVIRONMENT === 'development');

const IS_MAINTENANCE_ENABLED = process.env.REACT_APP_MAINTENANCE_VIEW === 'true';

const App = () => {
  useLayoutEffect(() => {
    handleUserEnvironmentUrl();

    // initialize google analytic
    const gaId = IS_DEV ? 'G-2JCGEY8DTG' : 'G-XGP6XKTHQF';
    InitializeGoogleAnalytics(gaId);
  }, []);

  const [isUserStatusChecked, setUserStatusChecked] = useState(false);

  const [totpFeatureEnabled] = useSetting('app_features', 'feature_allow_totp', false, false);

  const [isShowUSWarningModal, setIsShowUSWarningModal] = useState(false);
  const [allowSeparateEnv] = useSetting('app_features', 'feature_allow_separate_login_flow');
  const { isUSA: isUSAIpAddress } = useUserIPLocation();
  const userChooseToStayInUSApp = useMemo(() => getCacheUserWantsToStayInUSApp() === 'true', []);
  const userChooseToStayInInternationalApp = useMemo(
    () => getCacheUserWantsToStayInInternationalApp() === 'true',
    []
  );

  const history = useHistory();
  const queryClient = useQueryClient();
  const location = useLocation();

  const applyUserPreferences = (userData) => {
    const { currency } = userData.user;
    queryClient.setQueryData(USER_PREFERENCES, () => ({
      displayCurrency: currency?.toUpperCase(),
    }));
  };

  // Oauth2 params
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get('returnUrl');
  const appName = searchParams.get('app');

  useEffect(() => {
    if (returnUrl) {
      const { isUs } = getEnvByAppName(appName);
      sessionStorage.setItem(
        isUs ? OAUTH_SESSION_KEYS.US_RETURN_URL : OAUTH_SESSION_KEYS.ROW_RETURN_URL,
        returnUrl
      );
    }
  }, [appName, returnUrl]);

  useEffect(() => {
    document.title = process.env.REACT_APP_CLIENT_NAME || 'CL Card';
    getCurrentLanguage();
  }, []);

  const { data: userCardInfo, refetch: refetchUserCardInfo } = useQuery(USER_CARD_INFO, {
    enabled: false,
    onSettled: (userData, error) => {
      if (error) {
        resetUserSession();
        history.push(MAIN_DEFAULT_ROUTE);
      } else {
        // eslint-disable-next-line no-use-before-define
        getUserDetails();
      }
    },
  });

  const { refetch: getUserDetails, data: userDetails } = useQuery(USER_DETAILS_QUERY_KEY, {
    enabled: false,
    onSettled: (userData, error) => {
      if (error) {
        resetUserSession();
        history.push(MAIN_DEFAULT_ROUTE);
      } else {
        applyUserPreferences(userData);
        if (DEFAULT_ROUTES.includes(location.pathname)) {
          if (
            !getCacheUserFirstAccess() &&
            totpFeatureEnabled &&
            userDetails?.user?.totp_active &&
            userData?.user &&
            !userData?.user?.totp_enabled
          ) {
            history.push(`/onboarding/totp`, { email: userDetails?.user?.email });
          } else if (userCardInfo && userCardInfo?.hasCard) {
            history.push('/dashboard/card/details');
          } else {
            history.push('/dashboard/card');
          }
        }
      }
    },
  });

  const token = useMemo(() => getCachedUserToken(), [location?.pathname]);

  useEffect(() => {
    if (IS_MAINTENANCE_ENABLED) {
      history.push(`/maintenance`);
    }
  }, [IS_MAINTENANCE_ENABLED]);

  useEffect(() => {
    if (DEFAULT_ROUTES.includes(location.pathname) && token && !IS_MAINTENANCE_ENABLED) {
      if (
        !getCacheUserFirstAccess() &&
        totpFeatureEnabled &&
        userDetails?.user?.totp_active &&
        userDetails?.user &&
        !userDetails?.user?.totp_enabled
      ) {
        history.push(`/onboarding/totp`, { email: userDetails?.user?.email });
      } else if (userCardInfo && userCardInfo.hasCard) {
        history.push('/dashboard/card/details');
      } else {
        history.push('/dashboard/card');
      }
    }
  }, [
    location?.pathname,
    totpFeatureEnabled,
    userDetails?.user,
    userDetails?.user?.totp_enabled,
    userDetails?.user?.totp_active,
    IS_MAINTENANCE_ENABLED,
    userCardInfo,
  ]);

  // prevent accessing any routes if totp not enabled
  useEffect(() => {
    if (
      !getCacheUserFirstAccess() &&
      totpFeatureEnabled &&
      userDetails?.user?.totp_active &&
      userDetails?.user &&
      !userDetails?.user?.totp_enabled &&
      !ALLOWED_ROUTES_WITHOUT_TOTP.includes(location.pathname) &&
      location.pathname.indexOf('/password-reset') < 0 &&
      location.pathname.indexOf('/email-verification') < 0
    ) {
      history.push(`/onboarding/totp`, { email: userDetails?.user?.email });
    }
  }, [location.pathname, userDetails, totpFeatureEnabled]);

  useEffect(() => {
    if (token && !IS_MAINTENANCE_ENABLED) {
      cacheUserToken(token);
      refetchUserCardInfo();
    }
    setUserStatusChecked(true);
    if (!isBrowser) {
      handleWindowViewPort();
    }
    if (!getCacheUserFirstUsage()) {
      cacheUserFirstUsage();
    }
  }, [IS_MAINTENANCE_ENABLED]);

  useEffect(() => {
    if (
      !token &&
      allowSeparateEnv &&
      ((!isUSAIpAddress &&
        !userChooseToStayInUSApp &&
        window.location.hostname === process.env.REACT_APP_US_FE_BASE_HOSTNAME) ||
        (isUSAIpAddress &&
          !userChooseToStayInInternationalApp &&
          window.location.hostname === process.env.REACT_APP_FE_BASE_HOSTNAME))
    ) {
      setIsShowUSWarningModal(true);
    }
  }, [isUSAIpAddress, allowSeparateEnv, window.location.hostname, userChooseToStayInUSApp, token]);

  if (!isUserStatusChecked) {
    return (
      <>
        <ReactNotification />
        <GenericLoading />
      </>
    );
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.REACT_APP_RECAPTCHA}
      language={DEFAULT_LOCALE}>
      <Suspense fallback={<div />}>
        <ReactNotification />
        <Switch>
          <Route path="/promo">
            <Promo />
          </Route>
          <Route path="/login">
            <OauthLoginRedirectionComponent />
          </Route>
          <Route path="/oauth2/interaction/:oauthInteractionUid">
            <OauthRedirectionHandlerComponent />
          </Route>
          <Route path="/oauth2/consent/:oauthInteractionUid">
            <OauthAuthorizeComponent />
          </Route>
          <Route exact path="/timeout">
            <SessionTimeout />
          </Route>
          <Route path="/hint">
            <HintRouter />
          </Route>
          <Route path="/onboarding">
            <LoginRouter />
          </Route>
          <Route path="/showcase">
            <ShowCaseRouter />
          </Route>
          <Route path="/dashboard">
            <DashboardRouter />
          </Route>
          <Route path="/kyc">
            <KYCRoutes />
          </Route>
          <Route path="/withdrawal">
            <WithdrawalDeeplink />
          </Route>
          <Route path="/topup">
            <TopupDeeplink />
          </Route>
          <Route path="/manage-assets">
            <ManageAssetsDeeplink />
          </Route>
          <Route path="/order-card">
            <CardOrderDeeplink />
          </Route>
          <Route path="/order-physical-card">
            <CardOrderDeeplink />
          </Route>
          <Route path="/cashback">
            <CashbackDeeplink />
          </Route>
          <Route path="/set-pin">
            <SetPinDeeplink />
          </Route>
          <Route path="/maintenance">
            <Maintenance />
          </Route>
          <Route exact path="/">
            <Redirect to="/promo" />
          </Route>
          <Route path="*">
            <Redirect to={MAIN_DEFAULT_ROUTE} />
          </Route>
          {IS_DEV && <Route path="/icons" component={Icons} />}
        </Switch>
        {IS_DEV && isBrowser && (
          <span
            className="bg-light text-light text-f6 position-absolute p-3"
            style={{ opacity: 0.3, top: 0, left: 0, pointerEvents: 'none' }}>
            {getUrlEnvironment() === ACCOUNT_REGIONS.UK_OR_EU
              ? process.env.REACT_APP_API_BASE_URL
              : process.env.REACT_APP_US_API_BASE_URL || 'https://ledger.baanx.co.uk/'}
          </span>
        )}
        {/* This modal will open if user with international IP address opened US ledger app or user with USA IP address opened EU ledger app COB-211 */}
        {(!process.env.REACT_APP_FLAVOR || process.env.REACT_APP_FLAVOR === 'ledger') && (
          <UsAppWarningModal isVisible={isShowUSWarningModal} toggle={setIsShowUSWarningModal} />
        )}
      </Suspense>
    </GoogleReCaptchaProvider>
  );
};

export default App;
